import type { InsightItemDto, SectionInsightsDto } from './reportEnums.constants';

/** Partial insight item as stored on section rows or snapshots. */
export type SectionInsightsInput = {
  clinical?: Partial<InsightItemDto>;
  financial?: Partial<InsightItemDto>;
} | null;

const mergeInsightCard = (
  sectionCard: Partial<InsightItemDto> | undefined,
  snapshotCard: Partial<InsightItemDto> | undefined,
): InsightItemDto | undefined => {
  const hasSectionCard = sectionCard !== undefined;
  const hasSnapshotCard =
    Boolean(snapshotCard?.heading?.trim()) &&
    Boolean(snapshotCard?.body?.trim());

  if (!hasSectionCard && !hasSnapshotCard) {
    return undefined;
  }

  const overline = sectionCard?.overline ?? snapshotCard?.overline;

  return {
    ...(overline !== undefined ? { overline } : {}),
    heading: sectionCard?.heading ?? snapshotCard?.heading ?? '',
    body: sectionCard?.body ?? snapshotCard?.body ?? '',
    citationUrl: sectionCard?.citationUrl ?? snapshotCard?.citationUrl,
    citationLabel: sectionCard?.citationLabel ?? snapshotCard?.citationLabel,
  };
};

/**
 * Reads paired clinical/financial insights embedded in a section data snapshot.
 * @param dataSnapshot - Resolved section snapshot JSON.
 * @returns Snapshot insights or null when absent.
 */
export const readSectionInsightsFromSnapshot = (
  dataSnapshot: Record<string, unknown> | null | undefined,
): SectionInsightsInput => {
  if (
    !dataSnapshot ||
    typeof dataSnapshot.insights !== 'object' ||
    dataSnapshot.insights === null
  ) {
    return null;
  }
  return dataSnapshot.insights as SectionInsightsInput;
};

/**
 * Merges author-edited section insights with snapshot defaults for display.
 * Section overrides win per card; unset cards fall back to the snapshot.
 * @param sectionInsights - Persisted author overrides on the section row.
 * @param dataSnapshot - Resolved section snapshot JSON.
 * @returns Effective insights for rendering or null when both sources are empty.
 */
export const resolveEffectiveSectionInsights = (
  sectionInsights: SectionInsightsInput | undefined,
  dataSnapshot: Record<string, unknown> | null | undefined,
): SectionInsightsDto | null => {
  const fromSnapshot = readSectionInsightsFromSnapshot(dataSnapshot);
  if (!sectionInsights && !fromSnapshot) {
    return null;
  }
  const clinical = mergeInsightCard(
    sectionInsights?.clinical,
    fromSnapshot?.clinical,
  );
  const financial = mergeInsightCard(
    sectionInsights?.financial,
    fromSnapshot?.financial,
  );
  if (!clinical && !financial) {
    return null;
  }
  return {
    ...(clinical ? { clinical } : {}),
    ...(financial ? { financial } : {}),
  };
};
